import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for DieGroup module to handle 401 errors
const dieGroupAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for DieGroup module
dieGroupAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// DieGroup APIs
const getDieGroupList = (page, pagesize, search, ordering) => {
  return dieGroupAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/die-group/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieGroupById = id => {
  return dieGroupAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-group/${id}/`, {
    headers: authHeader(),
  });
};

const addDieGroup = formData => {
  return dieGroupAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-group/`,
    formData,
    { headers: authHeader() },
  );
};

const updateDieGroup = (id, formPayload) => {
  return dieGroupAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/die-group/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchDieGroup = (id, formPayload) => {
  return dieGroupAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/die-group/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteDieGroup = id => {
  return dieGroupAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/die-group/${id}/`,
    { headers: authHeader() },
  );
};

const getDieGroupDropdown = (filterKey) => {
  return dieGroupAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-group/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getDieGroupArchiveList = (page, pagesize, search, ordering) => {
  return dieGroupAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-group/archived/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieGroupArchiveById = id => {
  return dieGroupAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-group-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreDieGroup = ids => {
  return dieGroupAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-group/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveDieGroup = ids => {
  return dieGroupAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-group/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportDieGroup = formData => {
  return dieGroupAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-group/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return dieGroupAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-group/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return dieGroupAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-group/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return dieGroupAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-group/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const dieGroupService = {
  getDieGroupList,
  getDieGroupById,
  addDieGroup,
  updateDieGroup,
  patchDieGroup,
  deleteDieGroup,
  getDieGroupDropdown,
  getDieGroupArchiveList,
  getDieGroupArchiveById,
  restoreDieGroup,
  bulkArchiveDieGroup,
  bulkImportDieGroup,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default dieGroupService;
